import { useTaskWithErrorHandling } from "@/hooks/useTaskWithErrorHandling";
import { downloadRequestDocument } from "@/Api/PublicRequestApi";
import { PublicRequestOutput } from "@/Schemas/PublicRequest/PublicRequestOutput";
import { getCompanyHomeLabel, getCompanyHomeUrl } from "@/Utils/environment";
import { magicLinkPageColumnSx } from "@/Utils/MagicLink/pageLayout";
import { LoadingButton } from "@mui/lab";
import { Box, Button, CircularProgress, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState } from "react";
import { ArrowRightIcon, CheckmarkIcon, DownloadFileIcon } from "request-form-lib";
import { getCompanyPortalName } from "@/Utils/environment";

const juliFontFeatureSettings = { fontFeatureSettings: "'liga' off, 'clig' off" } as const;

const MOBILE_CTA_FOOTER_HEIGHT_PX = 88;

const downloadConfirmationLabelSx = {
    color: "#000",
    textAlign: "center",
    ...juliFontFeatureSettings,
    fontFamily: '"Juli Sans", Juli, sans-serif',
    fontSize: "15px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "148%",
    textTransform: "none",
} as const;

type PublicRequestSuccessViewProps = {
    publicRequestResult: PublicRequestOutput;
    getRecaptchaCode: () => Promise<string>;
};

export const PublicRequestSuccessView = ({
    publicRequestResult,
    getRecaptchaCode
}: PublicRequestSuccessViewProps) => {
    const theme = useTheme();
    const successColor = theme.palette.success.main;
    const [isDownloading, setIsDownloading] = useState(false);

    const [hasDownloadError, setHasDownloadError] = useState(false);
    const [genericErrorMessage, setGenericErrorMessage] = useState(() => "");

    const downloadRequestDocumentTask = useTaskWithErrorHandling({
        asyncFunction: async () => {
            setHasDownloadError(false);
            setIsDownloading(true);
            const recaptchaResponse = await getRecaptchaCode();
            await downloadRequestDocument({
                token: publicRequestResult.token,
                requestDocId: publicRequestResult.requestDocId,
                recaptcha: recaptchaResponse
            }, publicRequestResult.requestDocName);
            
            setIsDownloading(false);
        },
        onError: (message, errorCode, errors) => {
            setGenericErrorMessage(message);
            setIsDownloading(false);
            setHasDownloadError(true);
        },
        genericErrorMessage: "Nastala chyba pri sťahovaní. Skúste to prosím neskôr.",
    });

    const handleDownloadRequestDocument = () => {
        void downloadRequestDocumentTask.perform();
    };

    const renderCompanyHomeButton = (fullWidth: boolean) => (
        <Button
            component="a"
            href={getCompanyHomeUrl()}
            variant="contained"
            color="primary"
            endIcon={
                <ArrowRightIcon
                    width={18}
                    height={18}
                    color={theme.palette.primary.contrastText}
                />
            }
            sx={{
                typography: "button",
                fontWeight: theme.typography.fontWeightMedium,
                width: fullWidth ? "100%" : "fit-content",
                minWidth: fullWidth ? undefined : "91px",
                height: 48,
                minHeight: 48,
                borderRadius: "7px",
                py: theme.spacing(1.5),
                px: theme.spacing(2),
                textTransform: "none",
                whiteSpace: "nowrap",
            }}
        >
            {getCompanyHomeLabel()}
        </Button>
    );

    const portalName = getCompanyPortalName();

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    flex: 1,
                }}
            >
            <Box
                sx={{
                    ...magicLinkPageColumnSx,
                    maxWidth: { xs: "100%", md: "682px" },
                    minWidth: { xs: 0, md: 682 },
                }}
            >
                <Box
                    sx={{
                        backgroundColor: theme.palette.common.white,
                        width: "100%",
                        minHeight: { xs: "auto", md: "528px" },
                        borderRadius: "7px",
                        p: { xs: 3, md: theme.spacing(6) },
                        boxShadow: `20px 20px 50px 0px ${alpha(theme.palette.grey[900], 0.04)}`,
                        display: "flex",
                        flexDirection: "column",
                        gap: theme.spacing(4),
                        alignItems: "center",
                        justifyContent: "center",
                        boxSizing: "border-box",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: theme.spacing(3),
                        }}
                    >
                        <CheckmarkIcon width={64} height={64} color={successColor} />

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: theme.spacing(1.5),
                            }}
                        >
                            <Typography
                                variant="h1"
                                component="p"
                                textAlign="center"
                                sx={{
                                    fontSize: theme.typography.pxToRem(32),
                                    fontWeight: theme.typography.fontWeightBold,
                                    lineHeight: 1,
                                    letterSpacing: 0,
                                    color: successColor,
                                }}
                            >
                                Vaša žiadosť bola úspešne odoslaná
                            </Typography>

                            <Typography
                                variant="body1"
                                textAlign="center"
                                sx={{
                                    maxWidth: "560px",
                                    color: theme.palette.grey[800],
                                }}
                            >
                                O jej vybavení vás budeme informovať prostredníctvom e-mailu, SMS
                                správy alebo notifikácie v portáli {portalName}. Potvrdenie o
                                vytvorení žiadosti sme odoslali na vašu e-mailovú adresu.
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            width: "100%",
                            minHeight: theme.spacing(9),
                            borderRadius: "7px",
                            p: theme.spacing(2),
                            gap: theme.spacing(2),
                            display: "flex",
                            flexDirection: "column", // 👈 added
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#F2F2F2",
                        }}
                    >
                        <Box
                            sx={{
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: theme.spacing(2),
                                flexWrap: { xs: "wrap", md: "nowrap" },
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.common.black,
                                    minWidth: 0,
                                    textAlign: { xs: "center", md: "right" },
                                }}
                            >
                                Číslo poistnej zmluvy:{" "}
                                <Box
                                    component="span"
                                    sx={{
                                        fontWeight: theme.typography.fontWeightBold,
                                        overflowWrap: "anywhere",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {publicRequestResult.contractId || "-"}
                                </Box>
                            </Typography>

                            {publicRequestResult.requestDocId && (
                                <LoadingButton
                                    variant="outlined"
                                    color="secondary"
                                    loading={isDownloading}
                                    loadingPosition="start"
                                    loadingIndicator={<CircularProgress size={16} color="inherit" />}
                                    disabled={isDownloading}
                                    startIcon={
                                        !isDownloading ? (
                                            <DownloadFileIcon
                                                width={16}
                                                height={16}
                                                color="#000"
                                            />
                                        ) : undefined
                                    }
                                    onClick={handleDownloadRequestDocument}
                                    sx={{
                                        ...downloadConfirmationLabelSx,
                                        width: "fit-content",
                                        height: 40,
                                        borderRadius: "7px",
                                        py: theme.spacing(1.125),
                                        px: theme.spacing(1.5),
                                        gap: 1,
                                        whiteSpace: "nowrap",
                                        flexShrink: 0,
                                        backgroundColor: theme.palette.common.white,
                                        borderColor: theme.palette.grey[200],
                                        "&:hover": {
                                            backgroundColor: theme.palette.common.white,
                                            borderColor: theme.palette.grey[200],
                                        },
                                    }}
                                >
                                    {!isDownloading && "Stiahnuť potvrdenie"}
                                </LoadingButton>
                            )}
                        </Box>

                        {hasDownloadError && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.error.main,
                                    textAlign: "center",
                                    width: "100%",
                                }}
                            >
                                {genericErrorMessage}
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ display: { xs: "none", md: "block" } }}>
                        {renderCompanyHomeButton(false)}
                    </Box>
                </Box>
            </Box>
            </Box>

            <Box
                aria-hidden
                sx={{
                    display: { xs: "block", md: "none" },
                    flexShrink: 0,
                    height: `${MOBILE_CTA_FOOTER_HEIGHT_PX}px`,
                }}
            />

            <Box
                sx={{
                    display: { xs: "flex", md: "none" },
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1100,
                    backgroundColor: theme.palette.background.default,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    paddingX: "16px",
                    paddingTop: 2,
                    paddingBottom: "max(16px, env(safe-area-inset-bottom))",
                    width: "100%",
                    boxSizing: "border-box",
                }}
            >
                {renderCompanyHomeButton(true)}
            </Box>
        </Box>
    );
};
